import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';

/**
 * Fetches all booking enquiries from Firestore
 * Navigates through the hierarchical structure: year > month > date > enquiry ID
 * @returns {Promise<Array>} Array of all booking enquiries
 */
export const getAllBookingEnquiries = async () => {
  try {
    const enquiriesCollection = collection(db, 'bookingEnquiries');
    const yearDocs = await getDocs(enquiriesCollection);
    
    let allEnquiries = [];
    
    // Process each year document
    for (const yearDoc of yearDocs.docs) {
      const year = yearDoc.id;
      const yearData = yearDoc.data();
      
      // Process each month in the year
      for (const [month, monthData] of Object.entries(yearData)) {
        // Process each date in the month
        for (const [date, dateData] of Object.entries(monthData)) {
          // Process each enquiry for this date
          for (const [enquiryId, enquiryData] of Object.entries(dateData)) {
            // Add the enquiry to our result array with metadata
            allEnquiries.push({
              id: enquiryId,
              year,
              month,
              date,
              ...enquiryData,
              // Convert server timestamp to Date if needed
              createdAt: enquiryData.createdAt ? 
                (enquiryData.createdAt.toDate ? enquiryData.createdAt.toDate() : enquiryData.createdAt) : 
                new Date()
            });
          }
        }
      }
    }
    
    // Sort enquiries by createdAt, newest first
    allEnquiries.sort((a, b) => b.createdAt - a.createdAt);
    
    return allEnquiries;
  } catch (error) {
    throw error;
  }
};

/**
 * Update the status of a booking enquiry
 * @param {string} year - The year of the enquiry
 * @param {string} month - The month of the enquiry (01-12)
 * @param {string} date - The date of the enquiry (01-31)
 * @param {string} enquiryId - The unique ID of the enquiry
 * @param {string} newStatus - The new status to set
 * @returns {Promise<boolean>} Success indicator
 */
export const updateEnquiryStatus = async (year, month, date, enquiryId, newStatus) => {
  try {
    const docRef = doc(db, 'bookingEnquiries', year);
    
    // First get the current document to ensure it exists
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error(`Document ${year} not found`);
    }
    
    // Update just the status field in the nested structure
    await updateDoc(docRef, {
      [`${month}.${date}.${enquiryId}.status`]: newStatus,
      [`${month}.${date}.${enquiryId}.lastUpdated`]: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete an enquiry from Firestore
 * @param {string} year - The year the enquiry was created
 * @param {string} month - The month the enquiry was created
 * @param {string} date - The date the enquiry was created
 * @param {string} enquiryId - The ID of the enquiry to delete
 * @returns {Promise<boolean>} - A promise that resolves to true if successful
 */
export const deleteEnquiry = async (year, month, date, enquiryId) => {
  try {
    // First, let's ensure all parameters are provided
    if (!year || !month || !date || !enquiryId) {
      throw new Error("Missing required parameters for deleting enquiry");
    }

    // Reference to the year document (based on the structure in getAllBookingEnquiries)
    const docRef = doc(db, 'bookingEnquiries', year);
    
    // Get the current document to make sure it exists
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error(`Year document ${year} not found`);
    }
    
    // Use the updateDoc with a path to the specific enquiry
    const updatePath = `${month}.${date}.${enquiryId}`;
    
    // Create an update object that deletes the specific field
    const updateObj = {};
    updateObj[updatePath] = deleteField();
    
    // Update the document to remove the enquiry
    await updateDoc(docRef, updateObj);
    
    return true;
  } catch (error) {
    throw error;
  }
};
